import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import mockCategoriesData from '../../data/mockCategories.json';
import mockSynonymsData from '../../data/mockSynonyms.json';
import parametersData from '../../data/parameters.json';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  TextField,
  Avatar,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Badge
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import FavoriteButton from '../../components/olymp/FavoriteButton';
import CartButton from '../../components/olymp/CartButton';
import SensitivityBadge from '../../components/olymp/SensitivityBadge';
import DataExplorationDetail from '../../components/olymp/DataExplorationDetail';
import OmopMappingDetail from '../../components/olymp/OmopMappingDetail';
import TagsSynonymsDetail from '../../components/olymp/TagsSynonymsDetail';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import ScienceIcon from '@mui/icons-material/Science';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const HeaderSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ParameterDetail = () => {
  const { parameterId } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // User role - get from localStorage (set by header role selector)
  const userRole = localStorage.getItem('olymp_demo_role') || 'data_steward';

  // Load parameter from JSON data
  const [parameter, setParameter] = useState(null);

  useEffect(() => {
    // Find parameter from parameters.json
    const foundParameter = parametersData.parameters.find(p => p.id === parseInt(parameterId));
    if (foundParameter) {
      // Merge with additional mock data for complete parameter object
      setParameter({
        ...foundParameter,
        // Additional fields not in parameters.json
        approvedSynonyms: mockSynonymsData.confirmedSynonyms.map(syn => ({
          term: syn.term,
          language: syn.language
        })),
        frequencyMetrics: {
          caseCoverage: 85,
          stationCount: 12,
          totalCases: 40000,
          casesWithParameter: 34000,
          stationCoverage: [
            { name: 'ICU 1 - Interdisziplinär', percentage: 95, color: '#1976d2', caseCount: 18200 },
            { name: 'ICU 2 - Kardiologie', percentage: 92, color: '#42a5f5', caseCount: 14800 },
            { name: 'IMC 1 - Interdisziplinär', percentage: 88, color: '#90caf9', caseCount: 8100 },
            { name: 'Station 10A - Kardiologie', percentage: 45, color: '#bbdefb', caseCount: 6200 }
          ],
          avgMeasurementsPerCase: 42,
          medianMeasurementsPerCase: 28,
          totalMeasurements: 1450000,
          continuousCases: 28000,
          singleMeasurementCases: 5000,
          availableSince: foundParameter.availableSince || '2018-01',
          measurementPattern: {
            typical: 'Stündlich',
            avgInterval: '1.2h',
            mean: '2.8h',
            stdDev: '3.5h',
            interpretation: 'Hauptsächlich stündliche Messungen auf ICU-Stationen, vereinzelt 4-12h Intervalle auf Normalstationen.'
          }
        },
        engineerNotes: [
          {
            author: 'Max Mustermann',
            date: '2024-11-20',
            note: 'Werte werden automatisch aus dem Monitoring-System übernommen.'
          }
        ],
        sourceSystem: {
          name: 'ICU Monitoring System',
          updateFrequency: 'Kontinuierlich',
          lastSync: '2024-12-20 14:30',
          reliability: 'Sehr hoch'
        },
        comments: []
      });
    }
  }, [parameterId]);

  // State for tags - loaded from centralized JSON data
  // Includes validated/private categories
  const [tags, setTags] = useState(
    mockCategoriesData.validatedCategories.map(cat => ({
      name: cat.name,
      icon: cat.icon,
      validated: cat.validated,
      isPrivate: cat.isPrivate
    }))
  );

  // State for pending category suggestions - loaded from JSON
  const [pendingCategories, setPendingCategories] = useState(
    mockCategoriesData.pendingCategorySuggestions.map(cat => ({
      name: cat.name,
      icon: cat.icon,
      validated: false,
      isPrivate: cat.isPrivate
    }))
  );

  // Handler for OMOP mapping updates
  const handleMappingUpdate = (updatedParameter) => {
    setParameter(updatedParameter);
  };

  const handleAddToCart = () => {
    console.log('Add to cart:', parameter.id);
    // Show success message
  };

  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const handleCommentSubmit = () => {
    if (commentText.trim()) {
      console.log('Submit comment:', commentText);
      setCommentText('');
    }
  };

  const handleAddTag = (tagName, isPrivate, icon) => {
    if (isPrivate) {
      // Private tags go directly to validated tags
      const newTag = {
        name: tagName,
        icon: icon,
        validated: true,
        isPrivate: true
      };
      setTags([...tags, newTag]);
    } else {
      // Public tags go to pending categories
      const newPendingTag = {
        name: tagName,
        icon: icon,
        validated: false,
        isPrivate: false
      };
      setPendingCategories([...pendingCategories, newPendingTag]);
    }
  };

  const handleApproveTag = (tagName, icon) => {
    // Move from pending to validated
    const approvedTag = {
      name: tagName,
      icon: icon,
      validated: true,
      isPrivate: false
    };
    setTags([...tags, approvedTag]);
    // Remove from pending
    setPendingCategories(pendingCategories.filter(t => t.name !== tagName));
  };

  const handleRemoveTag = (tagName) => {
    // Remove from both validated tags and pending categories
    setTags(tags.filter(t => t.name !== tagName));
    setPendingCategories(pendingCategories.filter(t => t.name !== tagName));
  };

  // Icon mapping function
  const getIconComponent = (iconName) => {
    const iconMap = {
      'monitor_heart': MonitorHeartIcon,
      'local_hospital': LocalHospitalIcon,
      'vaccines': VaccinesIcon,
      'science': ScienceIcon
    };
    return iconMap[iconName] || null;
  };

  // Pastel colors for tags
  const getChipColor = (isValidated, isPrivate) => {
    if (isPrivate) return { bg: '#e3f2fd', border: '#42a5f5', color: '#1565c0' };
    if (isValidated) return { bg: '#e8f5e9', border: '#66bb6a', color: '#2e7d32' };
    return { bg: '#fff3e0', border: '#ffb74d', color: '#f57c00' };
  };

  // OMOP status styling
  const getOmopStatusConfig = (status) => {
    switch (status) {
      case 'validated':
        return {
          icon: CheckCircleIcon,
          label: 'OMOP Validiert',
          bg: '#e8f5e9',
          border: '#66bb6a',
          color: '#2e7d32'
        };
      case 'mapped':
        return {
          icon: CheckCircleIcon,
          label: 'OMOP Gemappt',
          bg: '#e3f2fd',
          border: '#42a5f5',
          color: '#1565c0'
        };
      case 'pending':
        return {
          icon: WarningIcon,
          label: 'OMOP Ausstehend',
          bg: '#fff3e0',
          border: '#ffb74d',
          color: '#f57c00'
        };
      default:
        return {
          icon: HelpOutlineIcon,
          label: 'OMOP Nicht gemappt',
          bg: '#ffebee',
          border: '#ef5350',
          color: '#c62828'
        };
    }
  };

  // Show loading state if parameter not loaded yet
  if (!parameter) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography variant="h6" color="textSecondary">
            Lade Parameter-Daten...
          </Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/catalog')}
          sx={{ mb: 2 }}
        >
          Zurück zum Katalog
        </Button>

        {/* Header Section */}
        <HeaderSection elevation={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  {parameter.name}
                </Typography>

                {/* Synonyms Indicator */}
                {parameter.approvedSynonyms && parameter.approvedSynonyms.length > 0 && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Bestätigte Synonyme
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {parameter.approvedSynonyms.slice(0, 5).map((syn, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={syn.language.toUpperCase()}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.6rem',
                                  backgroundColor: 'rgba(255,255,255,0.2)'
                                }}
                              />
                              <Typography variant="body2">{syn.term}</Typography>
                            </Box>
                          ))}
                          {parameter.approvedSynonyms.length > 5 && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                              +{parameter.approvedSynonyms.length - 5} weitere...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                    arrow
                    placement="bottom-start"
                  >
                    <Badge
                      badgeContent={parameter.approvedSynonyms.length}
                      color="primary"
                      sx={{
                        cursor: 'help',
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: 18,
                          minWidth: 18
                        }
                      }}
                    >
                      <TranslateIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                    </Badge>
                  </Tooltip>
                )}

                <FavoriteButton
                  isFavorite={isFavorite}
                  onClick={handleToggleFavorite}
                  size="medium"
                />
              </Box>
              <Typography variant="body1" color="textSecondary" paragraph>
                {parameter.description}
              </Typography>

              {/* Kategorien Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                  Kategorien
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {/* Validated/Private Categories (green/blue) */}
                  {tags.map((tag, index) => {
                    const CustomIconComponent = tag.icon ? getIconComponent(tag.icon) : null;
                    const chipColors = getChipColor(tag.validated, tag.isPrivate);

                    return (
                      <Chip
                        key={`validated-${index}`}
                        icon={
                          tag.isPrivate ? (
                            <LockIcon fontSize="small" sx={{ color: chipColors.color }} />
                          ) : CustomIconComponent ? (
                            <CustomIconComponent fontSize="small" sx={{ color: chipColors.color }} />
                          ) : (
                            <PublicIcon fontSize="small" sx={{ color: chipColors.color }} />
                          )
                        }
                        label={tag.name}
                        size="medium"
                        sx={{
                          backgroundColor: chipColors.bg,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: chipColors.border,
                          color: chipColors.color,
                          '& .MuiChip-label': {
                            color: chipColors.color
                          }
                        }}
                      />
                    );
                  })}

                  {/* Pending Categories (yellow/orange) */}
                  {pendingCategories.map((tag, index) => {
                    const CustomIconComponent = tag.icon ? getIconComponent(tag.icon) : null;
                    const chipColors = getChipColor(tag.validated, tag.isPrivate);

                    return (
                      <Chip
                        key={`pending-${index}`}
                        icon={
                          CustomIconComponent ? (
                            <CustomIconComponent fontSize="small" sx={{ color: chipColors.color }} />
                          ) : (
                            <PublicIcon fontSize="small" sx={{ color: chipColors.color }} />
                          )
                        }
                        label={tag.name}
                        size="medium"
                        sx={{
                          backgroundColor: chipColors.bg,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: chipColors.border,
                          color: chipColors.color,
                          '& .MuiChip-label': {
                            color: chipColors.color
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Status Section */}
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                  Status & Eigenschaften
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(() => {
                    const omopStatus = parameter.omopMapping?.status || parameter.omopStatus || 'unmapped';
                    const omopConfig = getOmopStatusConfig(omopStatus);
                    const OmopIconComponent = omopConfig.icon;
                    return (
                      <Chip
                        icon={<OmopIconComponent fontSize="small" sx={{ color: omopConfig.color }} />}
                        label={omopConfig.label}
                        size="small"
                        sx={{
                          backgroundColor: omopConfig.bg,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: omopConfig.border,
                          color: omopConfig.color,
                          '& .MuiChip-label': {
                            color: omopConfig.color,
                            fontWeight: 500
                          }
                        }}
                      />
                    );
                  })()}
                  <SensitivityBadge level={parameter.sensitivity} />
                  {parameter.usagePattern && (
                    <Chip
                      label={parameter.usagePattern}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(0,0,0,0.23)',
                        color: 'text.primary'
                      }}
                    />
                  )}
                  <Chip
                    label="Automatische Erfassung"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(0,0,0,0.23)',
                      color: 'text.primary'
                    }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Verfügbar seit {parameter.availableSince}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </HeaderSection>

        {/* Tabs Navigation */}
        <Paper elevation={1} sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Verfügbarkeit & Daten" />
            <Tab label="OMOP Mapping" />
            <Tab label="Tags & Synonyme" />
            <Tab label="Dokumentation" />
            <Tab label="Kommentare" />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <Box sx={{ mb: 3 }}>
          {/* Tab 0: Verfügbarkeit & Daten */}
          {activeTab === 0 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              {parameter.frequencyMetrics && (
                <DataExplorationDetail
                  data={parameter.frequencyMetrics}
                  sensitivity={parameter.sensitivity}
                  usagePattern={parameter.usagePattern}
                  parameterName={parameter.name}
                />
              )}
            </Paper>
          )}

          {/* Tab 1: OMOP Mapping */}
          {activeTab === 1 && parameter && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <OmopMappingDetail
                parameter={parameter}
                onMappingUpdate={handleMappingUpdate}
                userRole={userRole}
              />
            </Paper>
          )}

          {/* Tab 2: Tags & Synonyme */}
          {activeTab === 2 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <TagsSynonymsDetail
                tags={[...tags.map(t => t.name), ...pendingCategories.map(t => t.name)]}
                synonyms={parameter.synonyms || []}
                availableTags={['Vitalparameter', 'ICU', 'Monitoring', 'Laborparameter', 'BGA', 'COVID-19', 'Sepsis-Studie', 'Alte-Studie']}
                currentUser="Max Mustermann"
                userRole={userRole}
                onAddTag={handleAddTag}
                onApproveTag={handleApproveTag}
                onRemoveTag={handleRemoveTag}
              />
            </Paper>
          )}

          {/* Tab 3: Dokumentation */}
          {activeTab === 3 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                <InfoIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Dokumentation & Hinweise
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Quellsystem
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>System:</strong> {parameter.sourceSystem.name}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>Update-Frequenz:</strong> {parameter.sourceSystem.updateFrequency}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>Letzter Sync:</strong> {parameter.sourceSystem.lastSync}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>Zuverlässigkeit:</strong> {parameter.sourceSystem.reliability}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Data Engineer Notizen
              </Typography>
              <List>
                {parameter.engineerNotes.map((note, index) => (
                  <ListItem key={index} alignItems="flex-start" sx={{ backgroundColor: '#fff3e0', mb: 1, borderRadius: 1 }}>
                    <ListItemText
                      primary={note.note}
                      secondary={`${note.author} - ${note.date}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Tab 4: Kommentare */}
          {activeTab === 4 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                <CommentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Kommentare & Diskussion
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* Comment Input */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Kommentar hinzufügen..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={handleCommentSubmit}
                  disabled={!commentText.trim()}
                >
                  Kommentar senden
                </Button>
              </Box>

              {/* Comments List */}
              <List>
                {parameter.comments.map(comment => (
                  <Box key={comment.id} sx={{ mb: 2 }}>
                    <ListItem alignItems="flex-start" sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Avatar sx={{ mr: 2 }}>{comment.author[0]}</Avatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" component="span">
                              {comment.author}
                            </Typography>
                            <Chip label={comment.role} size="small" sx={{ ml: 1, height: 20 }} />
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                              {comment.date}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {comment.text}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {comment.replies && comment.replies.map((reply, idx) => (
                      <Box key={idx} sx={{ ml: 8, mt: 1 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'white' }}>
                          <Typography variant="caption" fontWeight="bold">
                            {reply.author}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                            {reply.date}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {reply.text}
                          </Typography>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Box>
    </MainLayout>
  );
};

export default ParameterDetail;
